import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import DashboardLink from 'insights/analytics-dashboards/DashboardLink';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import AddIcon from 'ui-library/icons/AddIcon';

import {AnalyticsParameterValueTemplateGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterValueTemplateGraphQL';
import {EmbeddableParameterType} from '../EmbeddingConstants';
import {TagSchemaDashboardLinkTableQuery} from './__generated/TagSchemaDashboardLinkTableQuery';
import DashboardLinkTableActionButtons from './DashboardLinkConfigTableActionButtons';
import EmbeddedParameterPillList from './EmbeddedParameterPillList';

type AnalyticsDashboard = ExtractByTypename<
  NonNullable<
    ExtractByTypename<
      TagSchemaDashboardLinkTableQuery['tagSchema'],
      'TagSchemaNode'
    >['analyticsDashboardLink']
  >['originalAnalyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsParameterConfig = AnalyticsDashboard['parameterConfigs'][number];

/**
 * This file contains the table components rendered for the embedded dashboard configuration table. There
 * are four table types, depending on the result of the query to get the embedded dashboard (successful
 * with embedded dashboard, successful without embedded dashboard, loading, or there was a permission or
 * query error). Each table type is styled to match other tables on the TagSchemaFileSettings page, using
 * simpleDivTable classes (see less/components/simple-div-table.less).
 *
 * - DashboardLinkConfigTableLoading - the query to load the embedded dashboard (if there is one)
 *   is loading, so show a throbber.
 * - DashboardLinkConfigTableErrored - there was an error loading the embedded dashboard, or the
 *   embedded dashboard is inaccessible to the current user. Shows an error message and a button to remove
 *   the embedded dashboard if the current user has write access to the tag schema.
 * - DashboardLinkConfigTableNoneEmbedded - there is no dashboard embedded in the tag schema.
 *   Shows button to embed a dashboard.
 * - DashboardLinkConfigTableDashboardEmbedded - there is a dashboard embedded in the tag schema.
 *   Shows options to edit or remove the embedded dashboard.
 *
 * Action buttons (to add, remove, or edit the linked dashboard) are hidden if the action handler
 * is undefined.
 */

/**
 * Normal simpleDivTable-row divs turn gray on hover. However, the background of parameter pills is the same
 * shade of gray, making it so that if you hover over a row with parameter pills (i.e. there is an embedded
 * dashboard that has parameters), you are no longer able to distinguish pill from row background.
 * This component disables the hover effect by overriding it with a white background on hover.
 */
function SimpleDivTableRowNoHoverChange(props: {children: ReactNode}): JSX.Element {
  return <div className={classNames('simpleDivTable-row', css(styles.noHoverColor))}>{props.children}</div>;
}

interface DashboardLinkConfigTableSkeletonProps {
  addEmbeddedDashboard?: () => void;
  children: JSX.Element;
}

function DashboardLinkConfigTableSkeleton(props: DashboardLinkConfigTableSkeletonProps): JSX.Element {
  return (
    <div
      className="simpleDivTable simpleDivTable--wide"
      {...getTestProps({component: 'DashboardTable', element: 'root'})}
    >
      <div className="simpleDivTable-header">
        <div className="simpleDivTable-headerCell simpleDivTable-header--title">
          Dashboard
          {props.addEmbeddedDashboard && (
            <Tooltip title="Add embedded dashboard">
              <AddIcon altText="Add embedded dashboard" onClick={props.addEmbeddedDashboard} />
            </Tooltip>
          )}
        </div>
        <div className="simpleDivTable-headerCell">Parameters</div>
        {/* Header of action button column */}
        <div className="simpleDivTable-headerCell simpleDivTable-cell--narrow" />
      </div>
      <div className="simpleDivTable-rowContainer">{props.children}</div>
    </div>
  );
}

export function DashboardLinkConfigTableLoading(): JSX.Element {
  return (
    <DashboardLinkConfigTableSkeleton>
      <div className="u-width-full">
        <CenteredHorizontalThrobber />
      </div>
    </DashboardLinkConfigTableSkeleton>
  );
}

interface DashboardLinkConfigTableErroredProps {
  errorMessage: string;
  removeEmbeddedDashboard?: () => void;
}

export function DashboardLinkConfigTableErrored(props: DashboardLinkConfigTableErroredProps): JSX.Element {
  return (
    <DashboardLinkConfigTableSkeleton>
      <SimpleDivTableRowNoHoverChange>
        <div className="simpleDivTable-cell">{props.errorMessage}</div>
        <div className="simpleDivTable-cell simpleDivTable-cell--narrow">
          <DashboardLinkTableActionButtons onRemove={props.removeEmbeddedDashboard} />
        </div>
      </SimpleDivTableRowNoHoverChange>
    </DashboardLinkConfigTableSkeleton>
  );
}

interface DashboardLinkConfigTableNoneEmbeddedProps {
  addEmbeddedDashboard?: () => void;
}

export function DashboardLinkConfigTableNoneEmbedded(
  props: DashboardLinkConfigTableNoneEmbeddedProps
): JSX.Element {
  return (
    <DashboardLinkConfigTableSkeleton addEmbeddedDashboard={props.addEmbeddedDashboard}>
      <SimpleDivTableRowNoHoverChange>
        <div className="simpleDivTable-cell">This schema does not have a dashboard</div>
      </SimpleDivTableRowNoHoverChange>
    </DashboardLinkConfigTableSkeleton>
  );
}

interface DashboardLinkConfigTableDashboardEmbeddedProps {
  embeddingType: EmbeddableParameterType;
  embeddedDashboard: AnalyticsDashboard;
  parameterValueTemplates: Array<AnalyticsParameterValueTemplateGraphQL>;
  removeEmbeddedDashboard?: () => void;
  editEmbeddedDashboard?: (config?: Pick<AnalyticsParameterConfig, 'id'>) => void;
}

export function DashboardLinkConfigTableDashboardEmbedded(
  props: DashboardLinkConfigTableDashboardEmbeddedProps
): JSX.Element {
  return (
    <DashboardLinkConfigTableSkeleton>
      <SimpleDivTableRowNoHoverChange>
        <div className="simpleDivTable-cell">
          <div className="simpleDivTable-cell-contents">
            <DashboardLink analyticsDashboard={props.embeddedDashboard} />
          </div>
        </div>
        <div className="simpleDivTable-cell">
          <EmbeddedParameterPillList
            embeddingType={props.embeddingType}
            configs={props.embeddedDashboard.parameterConfigs}
            valueTemplates={props.parameterValueTemplates}
            editEmbeddedDashboard={props.editEmbeddedDashboard}
          />
        </div>
        <div className="simpleDivTable-cell simpleDivTable-cell--narrow">
          <DashboardLinkTableActionButtons
            onRemove={props.removeEmbeddedDashboard}
            onEdit={props.editEmbeddedDashboard}
          />
        </div>
      </SimpleDivTableRowNoHoverChange>
    </DashboardLinkConfigTableSkeleton>
  );
}

const styles = StyleSheet.create({
  noHoverColor: {
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
});
